import React, { useEffect } from 'react';
import { GameState } from '../../types/game';
import { GameAction } from '../gameReducer';
import { useOnlineSnapshotHydration } from './useOnlineSnapshotHydration';
import { usePlayerAdoption } from './usePlayerAdoption';
import { useHostTakeoverSync } from './useHostTakeoverSync';
import { useSessionContract } from './useSessionContract';
import { useOnlineFallbackPoll } from './useOnlineFallbackPoll';
import { useAppResume } from './useAppResume';

interface Params {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  isHost: boolean;
  setIsHost: (value: boolean) => void;
  currentClientPlayerId: string | null;
  setCurrentClientPlayerId: (value: string | null) => void;
  lobbyPendingReadyChanges: Set<string>;
  setPendingReadyChanges: React.Dispatch<React.SetStateAction<Set<string>>>;
  joinGame: (
    gameId: string,
    options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean },
  ) => Promise<boolean>;
  modeHint?: GameState['mode'];
}

export function useGameEffects({
  gameState,
  dispatch,
  isHost,
  setIsHost,
  currentClientPlayerId,
  setCurrentClientPlayerId,
  lobbyPendingReadyChanges,
  setPendingReadyChanges,
  joinGame,
  modeHint,
}: Params) {
  useEffect(() => {
    setPendingReadyChanges(lobbyPendingReadyChanges);
  }, [lobbyPendingReadyChanges, setPendingReadyChanges]);
  const {
    log,
    syncLog,
    refreshSnapshot,
    requestAutoRefresh,
    handleOnlineEventApplied,
    handleOnlineStatusChange,
    handleOnlineSnapshot,
    handleOnlineServerError,
    lastOnlineEventAtRef,
    lastOnlineStatusRef,
  } = useOnlineSnapshotHydration({ gameState, dispatch });

  usePlayerAdoption({
    gameState,
    modeHint,
    currentClientPlayerId,
    setCurrentClientPlayerId,
    setIsHost,
    isHost,
    joinGame,
    log,
  });

  useHostTakeoverSync({ gameState, currentClientPlayerId, setIsHost });

  useSessionContract({ gameState, isHost });

  useEffect(() => {
    if (gameState.mode === 'ONLINE') return;
    if (!gameState.events || gameState.events.length === 0) return;
    const onlineLike = gameState.events.some(e => e.type === 'GAME_CREATED' || e.type === 'ADD_PLAYER' || e.type === 'PLAYER_READY' || e.type === 'SETTINGS_CHANGED' || e.type === 'ORDER_FINALIZED' || e.type === 'DICE_ROLL' || e.type === 'DICE_KEEP');
    if (onlineLike) {
  log('forceOnlineMode:heuristic', { events: gameState.events.length });
    }
  }, [gameState.events, gameState.mode, log]);
  const rehydratedTurnRef = React.useRef<string | null>(null);

  // Rehydrate current turn from PartyKit snapshot after hard refresh.
  useEffect(() => {
    if (gameState.mode === 'REAL' || !gameState.id) return;
    const tc = gameState.turnCycle;
    if (!tc?.activePlayerId || tc.rollCount <= 0) return;
    if ((tc.dice?.length ?? 0) > 0) return;
    const key = `${gameState.id}:${gameState.turnNumber}:${tc.activePlayerId}:${tc.rollCount}`;
    if (rehydratedTurnRef.current === key) return;
    rehydratedTurnRef.current = key;

    void refreshSnapshot('turn-rehydrate');
  }, [
    gameState.id,
    gameState.mode,
    gameState.turnNumber,
    gameState.turnCycle,
    gameState.turnCycle?.activePlayerId,
    gameState.turnCycle?.rollCount,
    gameState.turnCycle?.dice?.length,
    refreshSnapshot,
  ]);

  useOnlineFallbackPoll({
    gameState,
    isHost,
    lobbyPendingReadyChanges,
    refreshSnapshot,
    requestAutoRefresh,
    syncLog,
    lastOnlineEventAtRef,
    lastOnlineStatusRef,
  });

  useAppResume({ requestAutoRefresh });

  return {
    refreshSnapshot,
    onOnlineEventApplied: handleOnlineEventApplied,
    onOnlineStatusChange: handleOnlineStatusChange,
    onOnlineSnapshot: handleOnlineSnapshot,
    onOnlineServerError: handleOnlineServerError,
  };
}
